'use client';

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { GridSeatDenominated } from '@/lib/data-sources';

/**
 * Single source of truth for the V2 grid tile-value leaderboard query.
 *
 * Background: two consumers (the in-app `LeaderboardModal` and the
 * `ProfileLeaderboardSnippet` rendered inside the profile modal) both
 * needed this data. They each had their own `useQuery` call with the
 * same key (`['leaderboard', 'v2-tile']`) but DIFFERENT return shapes —
 * one wrapped the items in `{ data, total }`, the other returned the
 * raw `{ items }` envelope from the API.
 *
 * Whichever component mounted first seeded React Query's cache with
 * its own shape; the other then read an undefined property off the
 * unexpected object and silently fell back to "no players found".
 * In practice opening a profile first → modal showed the empty state.
 *
 * Centralising the queryFn here removes the ambiguity: callers only
 * see a normalised `V2LeaderboardUser[]` (defaulting to `[]` when
 * loading or on error) and the cache holds exactly one shape forever.
 */

/**
 * Subset of `MergedLeaderboardEntry` (see `lib/api/fetchers/portfolio.ts`)
 * that the leaderboard UI actually consumes. Kept intentionally loose
 * — every field the API returns is optional here so a backend addition
 * doesn't break the typecheck and a missing field on a stale cache
 * entry doesn't crash the renderer.
 */
export interface V2LeaderboardUser {
  /** Wallet address, lower-cased server-side. Some legacy paths still
   *  expose it under `id`; both are checked at the call site. */
  address?: string;
  id?: string;
  displayName?: string;
  avatarUrl?: string;
  level?: number;
  /** V2 grid seat count for this wallet at the time the API replied. */
  v2SeatCount?: number;
  /** Legacy alias — older indexer rows surfaced as `seatsHeld`. */
  seatsHeld?: number;
  /** Pre-computed total seat value in USDC 6dp micro-units (string
   *  because `bigint` doesn't survive JSON). When absent / zero, the
   *  caller re-sums `gridSeats` client-side using the live TAKEOVER
   *  price for non-stable tax tokens. */
  totalSeatValue?: string;
  /** Per-seat detail used for the client-side re-sum fallback. */
  gridSeats?: GridSeatDenominated[];
}

const QUERY_KEY = ['leaderboard', 'v2-tile'] as const;

export type V2TileLeaderboardQuery = UseQueryResult<V2LeaderboardUser[], Error>;

/**
 * Fetch the V2 grid tile-value leaderboard. Returns React Query's
 * full result so callers can read `isLoading` / `isError` / `refetch`
 * alongside `data`. `data` is the items array directly (not wrapped)
 * — read `data ?? []` for a safe default.
 */
export function useV2TileLeaderboard(): V2TileLeaderboardQuery {
  return useQuery<V2LeaderboardUser[], Error>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetch('/api/stats/leaderboard?type=v2&limit=500');
      if (res.status === 429) {
        // Lazy-import the rate-limit error so this hook stays safe to
        // mount inside server-component trees that haven't initialised
        // the rate-limit provider yet.
        const { RateLimitError } = await import('@/providers/rate-limit-provider');
        throw new RateLimitError();
      }
      if (!res.ok) throw new Error('Failed to fetch leaderboard');
      const json = (await res.json()) as { items?: V2LeaderboardUser[] };
      return json.items ?? [];
    },
    // 60s matches the API's `Cache-Control: max-age=60`; refetching
    // any sooner just hits the same cached payload.
    staleTime: 60_000,
  });
}
